import { Controller } from '@hotwired/stimulus'
import { put } from '../lib/request'
import { emit } from '../lib/events'

const AUTOSCROLL_EDGE = 60 // px from the top/bottom edge that starts scrolling
const AUTOSCROLL_MAX_SPEED = 14 // px per animation frame right at the edge
const FLIP_DURATION = 150 // ms

export default class extends Controller {
  static values = { type: String }
  static targets = ['node', 'handle']

  initialize = () => {
    this.nodeHandlers = new WeakMap()
    this.handleHandlers = new WeakMap()
  }

  // Moving a target with .after()/.before() (see dragOver/pointerMove) makes
  // it briefly leave and rejoin the DOM, which fires this connected callback
  // again on the very same node. Handlers are tracked per-node and torn down
  // in nodeTargetDisconnected so a single drag doesn't pile up duplicate
  // listeners on a node it moves several times.
  nodeTargetConnected = (target) => {
    target.draggable = true

    const handlers = {
      dragstart: event => this.dragStart(event, target),
      dragenter: this.dragEnter,
      dragover: event => this.dragOver(event, target),
      drop: this.drop,
      dragend: event => this.dragEnd(event, target),
    }
    this.nodeHandlers.set(target, handlers)

    target.addEventListener('dragstart', handlers.dragstart)
    target.addEventListener('dragenter', handlers.dragenter)
    target.addEventListener('dragover', handlers.dragover)
    target.addEventListener('drop', handlers.drop)
    target.addEventListener('dragend', handlers.dragend)

    Array.from(target.querySelectorAll('*')).forEach((ele) => { ele.draggable = false })
  }

  nodeTargetDisconnected = (target) => {
    const handlers = this.nodeHandlers.get(target)
    if (!handlers) return

    target.removeEventListener('dragstart', handlers.dragstart)
    target.removeEventListener('dragenter', handlers.dragenter)
    target.removeEventListener('dragover', handlers.dragover)
    target.removeEventListener('drop', handlers.drop)
    target.removeEventListener('dragend', handlers.dragend)
    this.nodeHandlers.delete(target)
  }

  // Touch (and mouse, at narrow viewports) reordering happens through this
  // handle instead of native HTML5 drag-and-drop, which never fires on touch
  // at all - that's why dragging the whole row never worked on mobile.
  // Only pointerdown is bound to the handle itself; move/up/cancel are bound
  // to the document for the duration of the drag (see pointerDown) rather
  // than relying on setPointerCapture, since capture is silently released
  // the moment the handle's own row - its ancestor - gets moved by a swap.
  handleTargetConnected = (handle) => {
    const pointerdown = event => this.pointerDown(event, handle)
    this.handleHandlers.set(handle, pointerdown)
    handle.addEventListener('pointerdown', pointerdown)
  }

  handleTargetDisconnected = (handle) => {
    const pointerdown = this.handleHandlers.get(handle)
    if (!pointerdown) return

    handle.removeEventListener('pointerdown', pointerdown)
    this.handleHandlers.delete(handle)
  }

  connect = () => {
    // Tracked at the container level (rather than per-node) so it keeps updating
    // even while the pointer is over a gap, padding, or a non-node element.
    this.element.addEventListener('dragover', this.trackPointer)
  }

  disconnect = () => {
    this.element.removeEventListener('dragover', this.trackPointer)
    this.stopAutoScroll()
  }

  // --- Native HTML5 drag-and-drop (desktop, dragging anywhere on the row) ---

  dragStart = (event, target) => {
    const elementId = this.beginDrag(target)
    event.dataTransfer.setData('text/plain', elementId)
  }

  dragEnter = (event) => {
    if (event.dataTransfer.types.includes('Files')) return

    event.stopPropagation()
    event.preventDefault()
  }

  // Moves the dragged node into place as soon as it crosses the midpoint of
  // whatever it's hovering over, so the rest of the list shifts aside live
  // and shows exactly where it will land instead of only revealing it on drop.
  dragOver = (event, target) => {
    if (event.dataTransfer.types.includes('Files')) return
    event.preventDefault()

    const dragging = this.draggingElement()
    if (!dragging || dragging === target) return

    const { top, height } = target.getBoundingClientRect()
    const placeAfter = event.clientY - top > height / 2
    const reference = placeAfter ? target.nextElementSibling : target.previousElementSibling

    if (reference === dragging) return

    this.animateMove(() => {
      if (placeAfter) target.after(dragging)
      else target.before(dragging)
    }, [dragging, target])
  }

  drop = (event) => {
    if (event.dataTransfer.types.includes('Files')) return
    event.stopPropagation()
    event.preventDefault()

    const dragging = this.draggingElement()
    if (!dragging) return

    this.committed = true
    this.performRequest(dragging)
  }

  // dragend always fires on the source node once the drag operation is over,
  // whether it was dropped successfully, dropped somewhere invalid, or
  // cancelled outright - so it's the one reliable place to clean up state.
  dragEnd = (event, target) => {
    this.endDrag(target)
  }

  trackPointer = (event) => {
    if (event.dataTransfer.types.includes('Files')) return

    event.preventDefault()
    this.pointerY = event.clientY
  }

  // --- Touch / pointer drag (mobile, via the handle only) ---

  pointerDown = (event, handle) => {
    const target = handle.closest('[data-reorder-target="node"]')
    if (!target) return

    event.preventDefault()

    this.activePointerId = event.pointerId
    this.touchOffsetY = 0
    this.lastPointerClientY = event.clientY

    this.beginDrag(target)

    // Lift the row above its siblings and let it track the finger directly -
    // there's no native drag-image on touch, so without this the row would
    // only ever hop between resting slots instead of feeling picked up.
    target.style.position = 'relative'
    target.style.zIndex = '30'
    target.style.pointerEvents = 'none'
    target.classList.add('shadow-lg')

    // Bound to the document rather than captured on the handle: the handle's
    // own row gets moved by a swap mid-drag, and moving an element briefly
    // detaches it, which silently drops pointer capture along with it.
    document.addEventListener('pointermove', this.pointerMove)
    document.addEventListener('pointerup', this.pointerUp)
    document.addEventListener('pointercancel', this.pointerCancel)
  }

  pointerMove = (event) => {
    if (event.pointerId !== this.activePointerId) return
    event.preventDefault()

    const dragging = this.draggingElement()
    if (!dragging) return

    this.pointerY = event.clientY

    this.touchOffsetY += event.clientY - this.lastPointerClientY
    this.lastPointerClientY = event.clientY
    dragging.style.transition = 'none'
    dragging.style.transform = `translateY(${this.touchOffsetY}px)`

    // dragging has pointer-events:none while lifted, so this finds whatever
    // row is underneath it rather than the dragging row itself.
    const elementBelow = document.elementFromPoint(event.clientX, event.clientY)
    const hovered = elementBelow && elementBelow.closest('[data-reorder-target="node"]')
    if (!hovered || hovered === dragging || !this.nodeTargets.includes(hovered)) return

    const { top, height } = hovered.getBoundingClientRect()
    const placeAfter = event.clientY - top > height / 2
    const reference = placeAfter ? hovered.nextElementSibling : hovered.previousElementSibling

    if (reference === dragging) return

    const draggingFirstTop = dragging.getBoundingClientRect().top
    const hoveredFirstRect = hovered.getBoundingClientRect()

    if (placeAfter) hovered.after(dragging)
    else hovered.before(dragging)

    // The dragging node stays glued to the finger instead of settling back
    // to neutral like a normal FLIP move - compensate the running offset by
    // however much the swap just shifted its natural (untransformed) resting
    // position, so it doesn't visibly jump at the moment of the swap.
    this.touchOffsetY -= dragging.getBoundingClientRect().top - draggingFirstTop
    dragging.style.transform = `translateY(${this.touchOffsetY}px)`

    this.settleFlip(hovered, hoveredFirstRect)
  }

  pointerUp = event => this.pointerEnd(event, true)

  pointerCancel = event => this.pointerEnd(event, false)

  pointerEnd = (event, committed) => {
    if (event.pointerId !== this.activePointerId) return

    this.activePointerId = null
    document.removeEventListener('pointermove', this.pointerMove)
    document.removeEventListener('pointerup', this.pointerUp)
    document.removeEventListener('pointercancel', this.pointerCancel)

    const dragging = this.draggingElement()
    if (!dragging) return

    dragging.style.position = ''
    dragging.style.zIndex = ''
    dragging.style.pointerEvents = ''
    dragging.style.transition = ''
    dragging.style.transform = ''
    dragging.classList.remove('shadow-lg')

    if (committed) {
      this.committed = true
      this.performRequest(dragging)
    }

    this.endDrag(dragging)
  }

  // --- Shared by both interaction models ---

  beginDrag = (target) => {
    target.classList.add('bg-gray-100', 'opacity-75')

    const elementId = this.nodeId(target)

    // Other controllers (e.g. collection_drop) - and, for a pointer drag,
    // our own handlers - locate the dragging node via this attribute rather
    // than dataTransfer, which can only be read on dragstart/drop.
    target.setAttribute('data-currently-dragging', elementId)

    // Remembered so a drag that ends up somewhere other than this list (e.g.
    // a product dropped onto a collection) can be snapped back instead of
    // leaving the row wherever the live-reorder preview last put it.
    this.originalNextSibling = target.nextElementSibling
    this.committed = false

    this.emitProduct()
    this.startAutoScroll()

    return elementId
  }

  // Only drop()/pointerEnd(..., true) commit the reorder. Anything else -
  // dropped onto a collection, dropped nowhere, or cancelled - reverts the
  // live-reorder preview so the row doesn't end up stuck wherever it passed
  // through.
  endDrag = (target) => {
    target.classList.remove('bg-gray-100', 'opacity-75')
    target.removeAttribute('data-currently-dragging')
    this.stopAutoScroll()

    if (!this.committed) {
      target.parentElement.insertBefore(target, this.originalNextSibling)
    }

    this.originalNextSibling = null
  }

  // FLIP: capture each node's position before the DOM mutation, then animate
  // from there back to its resting spot instead of letting it snap instantly.
  animateMove = (mutate, nodes) => {
    const firstRects = nodes.map(node => node.getBoundingClientRect())

    mutate()

    nodes.forEach((node, index) => this.settleFlip(node, firstRects[index]))
  }

  settleFlip = (node, firstRect) => {
    const deltaY = firstRect.top - node.getBoundingClientRect().top
    if (!deltaY) return

    node.style.transition = 'none'
    node.style.transform = `translateY(${deltaY}px)`
    node.offsetHeight // force a reflow so the transform above is applied before we transition it away
    node.style.transition = `transform ${FLIP_DURATION}ms ease`
    node.style.transform = ''
  }

  startAutoScroll = () => {
    this.dragScrollContainer = this.findScrollContainer()
    this.autoScrollFrame = requestAnimationFrame(this.tickAutoScroll)
  }

  stopAutoScroll = () => {
    if (this.autoScrollFrame) cancelAnimationFrame(this.autoScrollFrame)
    this.autoScrollFrame = null
    this.pointerY = null
    this.dragScrollContainer = null
  }

  // Runs every frame for the duration of the drag (rather than only on
  // dragover/pointermove) so the list keeps scrolling smoothly even while the
  // pointer holds still near an edge.
  tickAutoScroll = () => {
    if (this.pointerY != null && this.dragScrollContainer) {
      const { container, usesWindow } = this.dragScrollContainer
      const top = usesWindow ? 0 : container.getBoundingClientRect().top
      const bottom = usesWindow ? window.innerHeight : container.getBoundingClientRect().bottom
      const distanceFromTop = this.pointerY - top
      const distanceFromBottom = bottom - this.pointerY

      if (distanceFromTop < AUTOSCROLL_EDGE) {
        const amount = AUTOSCROLL_MAX_SPEED * (1 - Math.max(distanceFromTop, 0) / AUTOSCROLL_EDGE)
        if (usesWindow) window.scrollBy(0, -amount)
        else container.scrollTop -= amount
      }
      else if (distanceFromBottom < AUTOSCROLL_EDGE) {
        const amount = AUTOSCROLL_MAX_SPEED * (1 - Math.max(distanceFromBottom, 0) / AUTOSCROLL_EDGE)
        if (usesWindow) window.scrollBy(0, amount)
        else container.scrollTop += amount
      }
    }

    this.autoScrollFrame = requestAnimationFrame(this.tickAutoScroll)
  }

  // The desktop products/collections lists are themselves the scrollable
  // element, but that's not universal - the mobile product list scrolls the
  // whole page instead - so walk up from it to find whatever actually
  // scrolls, falling back to the window.
  findScrollContainer = () => {
    let node = this.element

    while (node && node !== document.body) {
      const style = getComputedStyle(node)
      if (node.scrollHeight > node.clientHeight && /(auto|scroll)/.test(style.overflowY)) {
        return { container: node, usesWindow: false }
      }
      node = node.parentElement
    }

    return { container: document.scrollingElement || document.documentElement, usesWindow: true }
  }

  draggingElement = () => this.element.querySelector('[data-currently-dragging]')

  performRequest = (element) => {
    const params = this.neighborIds(element)

    return put(element.dataset.url, { body: JSON.stringify(params) })
  }

  // The server resolves rank neighbors from these ids itself, so a plain
  // DOM-order index is no longer enough - it doesn't survive pagination
  // and doesn't tell the server which two ranks to fit between.
  neighborIds = (element) => {
    const nodes = this.nodeTargets
    const index = nodes.indexOf(element)
    const beforeNode = index > 0 ? nodes[index - 1] : null
    const afterNode = index >= 0 && index < nodes.length - 1 ? nodes[index + 1] : null

    return {
      before_id: this.nodeId(beforeNode),
      after_id: this.nodeId(afterNode),
    }
  }

  nodeId = (node) => {
    if (!node) return null

    return node.getAttribute('data-product-id') || node.getAttribute('data-collection-id')
  }

  emitProduct = () => {
    if (this.typeValue === 'products') {
      emit('product:dragged', {
        bubbles: true,
        detail: { type: this.typeValue },
      })
    }
  }
}
